import {
  Mail,
  Phone,
  MapPin
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareFacebook,
  faTwitter,
  faLinkedin,
  faSquareInstagram,
  faYoutube,
  faXing,
} from "@fortawesome/free-brands-svg-icons";
import { Skeleton } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import "../styles/Firmendaten.css";
import HtmlRenderer from "../components/HtmlRenderer";
import useFetchPaginatedElements from "../fetchFunction/OverviewFetch";

const socialMediaIcons = {
  twitter: faTwitter,
  facebook: faSquareFacebook,
  linkedin: faLinkedin,
  youtube: faYoutube,
  instagram: faSquareInstagram,
  xing: faXing,
};

const validateFirmendaten = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data.map((item) => ({
    id: item.id,
    headlines: item.headlines || "Keine Überschrift verfügbar",
    texts: item.text ? [item.text] : ["Kein Text verfügbar"],
    imageUrl: item.image_urls || null,
    adress: item.adress || "Keine Adresse verfügbar",
    email: item.email || "Keine E-Mail-Adresse verfügbar",
    phone: item.phonenumber || "Keine Telefonnummer verfügbar",
    socialMedia: item.socialMedia || {},
  }));
};

const renderSocialMedia = (socialMedia, styling) => {
  const socialMediaEntries = Object.entries(socialMedia);

  if (socialMediaEntries.length === 0) {
    return <p>Keine Social-Media-Profile verfügbar.</p>;
  }

  return (
    <div className="unnpcw-container social-media-container">
      {socialMediaEntries.map(([platform, profiles]) => {
        const icon = socialMediaIcons[platform];
        return (
          <div key={platform} className="social-media-item">
            <h4 className="social-media-title">{platform.toUpperCase()}</h4>
            {profiles.map((profile, index) => (
              <div key={index} className="profile-link-container">
                {icon && (
                  <FontAwesomeIcon
                    icon={icon}
                    style={{ color: styling.iconColor, marginRight: "5px" }}
                    size="lg"
                  />
                )}
                <a
                  href={profile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: styling.linkColor,
                    textDecoration: "none",
                    marginBottom: "5px",
                  }}
                >
                  {profile.name || "Profil"}
                </a>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const FirmendatenOverview = ({ styling, companyAccessname }) => {
  const {
    loading,
    error,
    firmendaten: firmendatenData,
  } = useFetchPaginatedElements(companyAccessname, "firmendaten", 1, 3);

  const validFirmendaten = validateFirmendaten(firmendatenData);

  const replaceLinkColor = (text, linkColor) =>
    text.replace(/\$\{linkColor\}/g, linkColor);

  if (loading) {
    return (
      <div className="chapter-entry" style={styling.chapterEntry}>
        <Skeleton
          variant="text"
          sx={{ fontSize: "1rem", width: "100%", height: 30 }}
        />
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="100%" height={20} />
      </div>
    );
  }

  if (error) return <p>Error: {error}</p>;
  if (validFirmendaten.length === 0)
    return <p>Keine Firmendaten verfügbar.</p>;

  return (
    <div>
      {validFirmendaten.map((firmendaten, index) => (
        <div key={index} className="chapter-entry" style={styling.chapterEntry}>
          {firmendaten.headlines && (
            <h3 className="chapter-title" style={styling.chapterTitle}>
              {firmendaten.headlines}
            </h3>
          )}
          {firmendaten.imageUrl && (
            <div className="firmendaten-image-container">
              <img
                src={firmendaten.imageUrl}
                alt="Firmendaten Bild"
                className="firmendaten-image"
              />
            </div>
          )}

          <div className="firmendaten-text">
            {firmendaten.texts && (
              <HtmlRenderer
                texts={firmendaten.texts.map((text) =>
                  replaceLinkColor(text, styling.linkColor)
                )}
                sanitizeHtml={true}
              />
            )}
          </div>

          <div className="firmeninfo-section">
            {firmendaten.adress && (
              <p className="icon-text">
                <MapPin
                  className="icon"
                  style={{ color: styling.iconColor, marginRight: "6px" }}
                  size={16}
                />
                {firmendaten.adress}
              </p>
            )}
            {firmendaten.phone && (
              <p className="icon-text">
                <Phone
                  className="icon"
                  style={{ color: styling.iconColor, marginRight: "6px" }}
                  size={16}
                />
                {firmendaten.phone}
              </p>
            )}
            {firmendaten.email && (
              <p className="icon-text">
                <Mail
                  className="icon"
                  style={{ color: styling.iconColor, marginRight: "6px" }}
                  size={16}
                />
                {firmendaten.email}
              </p>
            )}
          </div>

          <div className="social-media-section">
            {renderSocialMedia(firmendaten.socialMedia, styling)}
          </div>
        </div>
      ))}
    </div>
  );
};

FirmendatenOverview.propTypes = {
  styling: PropTypes.shape({
    textColor: PropTypes.string,
    linkColor: PropTypes.string,
    linkHoverColor: PropTypes.string,
    chapterEntry: PropTypes.object,
    chapterTitle: PropTypes.object,
    iconColor: PropTypes.string,
  }),
  companyAccessname: PropTypes.string.isRequired,
};

export default FirmendatenOverview;
